/**
 * Liquid Glass Card Component
 * Implements Apple's Liquid Glass design principles
 * 
 * Features:
 * - Real-time depth and transparency
 * - Motion-responsive effects
 * - Accessibility-first approach
 * - Physically accurate lensing
 */

import { forwardRef } from 'react'
import { Box, type BoxProps } from '@mui/material'
import {
  createLiquidGlassEffect,
  getResponsiveLiquidGlass,
  type LiquidGlassVariant,
  type LiquidGlassLayer,
  type MotionPreference
} from '../../tokens'
import { getThemeColor } from '../../tokens/colors'

export interface LiquidGlassCardProps extends Omit<BoxProps, 'variant'> {
  /**
   * Glass effect variant
   * @default 'regular'
   */
  variant?: LiquidGlassVariant
  
  /**
   * Depth layer for spatial hierarchy
   * @default 'surface'
   */
  layer?: LiquidGlassLayer
  
  /**
   * Enable interactive effects (hover, focus, etc.)
   * @default true
   */
  interactive?: boolean
  
  /**
   * Enable glow effect on hover
   * @default true
   */
  glowOnHover?: boolean
  
  /**
   * Enable scale effect on hover
   * @default true
   */
  scaleOnHover?: boolean
  
  /**
   * Custom border radius
   */
  borderRadius?: string
  
  /**
   * Motion preference override
   * If not provided, will use system preference
   */
  motionPreference?: MotionPreference
  
  /**
   * Enable responsive glass effects
   * @default true
   */
  responsive?: boolean
  
  /**
   * Content padding
   * @default 3
   */
  padding?: number | string
}

/**
 * LiquidGlassCard - A card component implementing Apple's Liquid Glass design principles
 * 
 * @example
 * ```tsx
 * <LiquidGlassCard variant="prominent" layer="elevated">
 *   <Typography variant="h4">Glass Card</Typography>
 *   <Typography variant="body1">Beautiful liquid glass effect</Typography>
 * </LiquidGlassCard>
 * ```
 */
export const LiquidGlassCard = forwardRef<HTMLDivElement, LiquidGlassCardProps>(
  ({
    variant = 'regular',
    layer = 'surface',
    interactive = true,
    glowOnHover = true,
    scaleOnHover = true,
    borderRadius,
    motionPreference,
    responsive = true,
    padding = 3,
    children,
    sx,
    ...props
  }, ref) => {
    // Detect motion preference if not provided
    const finalMotionPreference = motionPreference || 
      (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches 
        ? 'reduced' 
        : 'full')
    
    // Create glass effect styles
    const glassStyles = responsive 
      ? getResponsiveLiquidGlass(variant, layer, 'light', finalMotionPreference)
      : createLiquidGlassEffect(variant, layer, 'light', finalMotionPreference, {
          interactive,
          glowOnHover,
          scaleOnHover,
          ...(borderRadius && { customBorderRadius: borderRadius }),
        })
    
    return (
      <Box
        ref={ref}
        sx={{
          ...glassStyles,
          padding,
          // Ensure content sits above the ::before glass overlay.
          // Scope to DIRECT children only — using '& *' forced position:relative
          // onto every descendant, which breaks components that rely on absolute
          // positioning (e.g. MUI outlined inputs' <fieldset>/<legend>, collapsing
          // the border + hiding labels). Direct children establish stacking contexts
          // above the overlay; their descendants render correctly within them.
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
          // Add subtle content backdrop for better readability
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: getThemeColor('overlay.subtle'),
            borderRadius: 'inherit',
            zIndex: 0,
          },
          ...sx,
        }}
        {...props}
      >
        {children}
      </Box>
    )
  }
)

LiquidGlassCard.displayName = 'LiquidGlassCard'

export default LiquidGlassCard
